import React, { useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBack from '@mui/icons-material/ArrowBack';
import SideBar from '../../uiParts/menu/SideBar';
import { usePageLayout } from '../../../hooks/usePageLayout';
import CustomLoading from '../../uiParts/form/CustomLoading';
import { useApiPageAccess } from '../../../hooks/useApiPageAccess';
import { apiHosts, firebaseProjectIds } from '../../../constants/enum';
import QuickStartGuide from './QuickStartGuide';
import ApiReference from './ApiReference';
import MetaTitle from '../../utils/MetaTitle';
import { gray40, gray80, gray100, red50, white } from '../../../constants/colors';
import {
  boldXxl,
  regularMd,
  regularSm,
  regularXs,
  spHeaderTitle,
} from '../../../constants/location';

const TAB_INDEX_QUICK_START = 0;
const TAB_INDEX_REFERENCE = 1;

const styles = {
  headerTitleWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  headerTitle: {
    ...boldXxl,
    color: gray100,
    '@media screen and (max-width:960px)': {
      ...spHeaderTitle,
    },
  },
  tabsWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    margin: '0 32px',
    '@media screen and (max-width:960px)': {
      margin: '0 16px',
    },
  },
  tab: {
    alignItems: 'center',
    backgroundColor: gray40,
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    height: '24px',
    justifyContent: 'center',
    width: '120px',
  },
  tabActive: {
    backgroundColor: gray100,
  },
  tabText: {
    ...regularXs,
    color: gray100,
  },
  tabTextActive: {
    color: white,
  },
  tableSection: {
    backgroundColor: white,
    border: '1px solid' + gray40,
    borderRadius: '4px',
    boxSizing: 'border-box',
    margin: '0 32px 32px',
    padding: '24px',
    textAlign: 'left' as const,
    width: 'calc(100% - 64px)',
    '@media screen and (max-width:960px)': {
      border: 'none',
      borderRadius: 0,
      margin: '0 0 24px',
      padding: '16px',
      width: '100%',
    },
  },
  messageWrapper: {
    margin: '24px 32px',
    padding: '24px',
    backgroundColor: white,
    border: '1px solid' + gray40,
    borderRadius: '4px',
    '@media screen and (max-width:960px)': {
      margin: '16px',
      padding: '16px',
    },
  },
  messageText: {
    ...regularMd,
    color: gray100,
  },
  errorText: {
    ...regularSm,
    color: red50,
  },
  notAvailableLabel: {
    ...regularSm,
    color: gray80,
    marginBottom: '8px',
  },
};

/**
 * 環境別の API ホスト
 * `REACT_APP_API_HOST` が設定されていればそれを優先、なければ Firebase プロジェクトから推定する
 */
const resolveApiHost = (): string => {
  const explicit = process.env.REACT_APP_API_HOST;
  if (explicit) return explicit;
  const projectId = process.env.REACT_APP_GCP_PROJECT_ID ?? '';
  if (projectId === firebaseProjectIds.prd) return apiHosts.prd;
  if (projectId === firebaseProjectIds.stg) return apiHosts.stg;
  return apiHosts.local;
};

const ApiDocsPage: React.FC = () => {
  const { sideBarCollapsed, toggleSidebar, styles: layoutStyles } = usePageLayout();
  const [tabIndex, setTabIndex] = useState(TAB_INDEX_QUICK_START);
  const navigate = useNavigate();
  const { loading, errorText, apiEnabled, canAccess, startDateText } = useApiPageAccess({
    allowOperatorWithoutCompany: true,
  });

  const host = resolveApiHost();

  return (
    <Box sx={layoutStyles.pageWrapper}>
      <MetaTitle customTitle="API ドキュメント" />
      <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
        <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
      </Box>
      <Box sx={[layoutStyles.pageContent, sideBarCollapsed && layoutStyles.pageContentCollapsed]}>
        <Box sx={layoutStyles.header}>
          <Box sx={styles.headerTitleWrapper}>
            <IconButton
              aria-label="APIキー管理に戻る"
              onClick={(): void => navigate('/company-settings/api-keys')}
            >
              <ArrowBack sx={{ fontSize: '24px', color: gray80 }} />
            </IconButton>
            <Typography sx={styles.headerTitle}>API ドキュメント</Typography>
          </Box>
        </Box>

        {loading ? (
          <CustomLoading loading />
        ) : errorText ? (
          <Box sx={styles.messageWrapper}>
            <Typography sx={styles.errorText}>{errorText}</Typography>
          </Box>
        ) : !apiEnabled ? (
          <Box sx={styles.messageWrapper}>
            <Typography sx={styles.notAvailableLabel}>API 機能 未契約</Typography>
            <Typography sx={styles.messageText}>
              この企業では API 機能が有効化されていません。事務局までお問い合わせください。
            </Typography>
          </Box>
        ) : !canAccess ? (
          <Box sx={styles.messageWrapper}>
            <Typography sx={styles.notAvailableLabel}>利用開始日まで待機中</Typography>
            <Typography sx={styles.messageText}>
              API 機能の利用開始日（{startDateText}
              ）にまだ到達していません。開始日以降にアクセスしてください。
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={styles.tabsWrapper}>
              <Box
                sx={[styles.tab, tabIndex === TAB_INDEX_QUICK_START && styles.tabActive]}
                onClick={(): void => setTabIndex(TAB_INDEX_QUICK_START)}
              >
                <Typography
                  sx={[styles.tabText, tabIndex === TAB_INDEX_QUICK_START && styles.tabTextActive]}
                >
                  クイックスタート
                </Typography>
              </Box>
              <Box
                sx={[styles.tab, tabIndex === TAB_INDEX_REFERENCE && styles.tabActive]}
                onClick={(): void => setTabIndex(TAB_INDEX_REFERENCE)}
              >
                <Typography
                  sx={[styles.tabText, tabIndex === TAB_INDEX_REFERENCE && styles.tabTextActive]}
                >
                  リファレンス
                </Typography>
              </Box>
            </Box>
            <Box sx={styles.tableSection}>
              {tabIndex === TAB_INDEX_QUICK_START && <QuickStartGuide environmentHost={host} />}
              {tabIndex === TAB_INDEX_REFERENCE && <ApiReference environmentHost={host} />}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default ApiDocsPage;
